import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useApiQuery, type Plan } from 'grabbitbot-legacy-ui'
import { paymentClient } from '../../api'
import { usePaymentActions, usePaymentBasics } from './paymentFlow'
import { PaymentPage, PlanCard } from './paymentShared'
import { failureText } from '../../api/errorMessages'

/**
 * `/payment/subscriptions[/:planId]` — legacy `PlanListComponent`: every
 * plan with its subscription state and actions. With `:planId` the named
 * plan is shown first and highlighted; a plan missing from the list (a
 * hidden plan) is fetched on its own (`GET /payment/plans/{id}`) and added,
 * as legacy did.
 */
export function PlanListPage() {
  const { planId } = useParams<{ planId?: string }>()
  const basics = usePaymentBasics()
  const { generation, refetch, method, billing } = basics
  const actions = usePaymentActions()
  const plans = useApiQuery(() => paymentClient.listPlans(), [generation])
  const list = Array.isArray(plans.data) ? plans.data : null
  const [extra, setExtra] = useState<Plan | null>(null)
  const [extraError, setExtraError] = useState<unknown>(null)

  const inList = Boolean(planId && list?.some((p) => p.id === planId))
  useEffect(() => {
    setExtra(null)
    setExtraError(null)
    if (!planId || !list || inList) return
    let cancelled = false
    paymentClient
      .viewPlan(planId)
      .then((plan) => {
        if (!cancelled) setExtra(plan && typeof plan === 'object' ? plan : null)
      })
      .catch((err: unknown) => {
        if (!cancelled) setExtraError(err ?? true)
      })
    return () => {
      cancelled = true
    }
  }, [planId, list, inList])

  const all = [...(list ?? []), ...(extra ? [extra] : [])]
  const ordered = planId ? [...all.filter((p) => p.id === planId), ...all.filter((p) => p.id !== planId)] : all

  return (
    <PaymentPage title="Subscriptions">
      {plans.loading && <p>Loading subscriptions…</p>}
      {plans.error && (
        <p className="portal-notice portal-notice--error" role="alert">
          {failureText(plans.error, 'Could not load subscriptions')}
        </p>
      )}
      {extraError !== null && (
        <p className="portal-notice portal-notice--warning" role="alert">
          No such subscription plan.
        </p>
      )}
      {list && ordered.length === 0 && <p className="portal-text-muted">No subscription plans.</p>}
      {ordered.map((plan) => (
        <PlanCard
          key={plan.id ?? plan.name}
          plan={plan}
          method={method}
          billing={billing}
          actions={actions}
          onChanged={refetch}
          highlighted={Boolean(planId && plan.id === planId)}
          allPlans={all}
        />
      ))}
    </PaymentPage>
  )
}
